import { useState } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Selo } from '../../componentes/base';
import { Entrada, Rotulo } from '../../componentes/formulario';
import { email as emailFmt } from '../../nucleo/formato';
import { discretoAtivo } from '../../nucleo/discreto';

/**
 * Lote crm6, item 13: Para, CC e CCO do "Criar e-mail" com vários endereços cada, os da negociação como sugestão e o endereço
 * livre aceito. Quem está fora dos contatos da negociação aparece marcado; antes de enviar, a tela pede a confirmação (o
 * servidor exige `confirmar_externos` e grava os de fora na auditoria).
 */
export const MAX_POR_CAMPO = 10;
export const MAX_DESTINOS = 20;
const FORMATO = /^[^\s@<>(),;:"[\]]+@[^\s@<>(),;:"[\]]+\.[^\s@<>(),;:"[\]]{2,}$/;

/** Os endereços digitados (separados por vírgula, ponto e vírgula ou espaço), em minúsculas e sem repetir. Função pura. */
export function separarEmails(texto: string): { validos: string[]; invalidos: string[] } {
  const partes = texto.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
  const validos = [...new Set(partes.filter((x) => x.length <= 254 && FORMATO.test(x)))];
  return { validos, invalidos: partes.filter((x) => !validos.includes(x)) };
}

/** Os endereços fora dos contatos da negociação, sem repetir. Função pura. */
export function externosDoEnvio(listas: string[][], internos: string[]): string[] {
  const dentro = new Set(internos.map((x) => x.toLowerCase()));
  return [...new Set(listas.flat().map((x) => x.toLowerCase()))].filter((x) => !dentro.has(x));
}

/**
 * Lote crm6, item 14: as etiquetas de tratamento do contato nos modelos de e-mail. `{{tratamento}}` é Sr. ou Sra. (vazio no
 * neutro ou sem escolha); `{{o/a}}` é o final das palavras ("Prezad{{o/a}}"): o, a ou a forma neutra o(a). Função pura.
 */
export function tagsDeTratamento(tratamento: string | null | undefined): { tratamento: string; 'o/a': string } {
  if (tratamento === 'sr' || tratamento === 'sra') return { tratamento: i18n.t(`cadastros:crm.tratamento.${tratamento}`), 'o/a': tratamento === 'sr' ? 'o' : 'a' };
  return { tratamento: '', 'o/a': 'o(a)' };
}

export function CampoEmails({
  id,
  rotulo,
  dado,
  valor,
  aoMudar,
  sugestoes,
  internos,
  obrigatorio,
  ajuda,
}: {
  id: string;
  rotulo: string;
  dado: string;
  valor: string[];
  aoMudar: (v: string[]) => void;
  sugestoes: string[];
  internos: string[];
  obrigatorio?: boolean;
  ajuda?: string;
}) {
  const { t } = useTranslation('pipeline');
  const [texto, setTexto] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const dentro = new Set(internos.map((x) => x.toLowerCase()));
  const livres = sugestoes.filter((x) => !valor.includes(x.toLowerCase()));
  const confirmar = (bruto = texto) => {
    const { validos, invalidos } = separarEmails(bruto);
    const novos = [...new Set([...valor, ...validos])];
    if (novos.length > MAX_POR_CAMPO) return setErro(t('crm.crm6.email.demaisNoCampo', { max: MAX_POR_CAMPO }));
    setErro(invalidos.length ? t('crm.crm6.email.invalido', { email: emailFmt(invalidos[0]) }) : null);
    if (validos.length) aoMudar(novos);
    setTexto(invalidos.join(' '));
  };
  return (
    <div data-email-destinos={dado}>
      <Rotulo id={id} rotulo={rotulo} obrigatorio={obrigatorio} erro={erro ?? undefined} ajuda={ajuda}>
        {valor.length > 0 && (
          <ul className="flex flex-wrap gap-1.5" aria-label={rotulo}>
            {valor.map((x) => (
              <li key={x} className="flex items-center gap-1 rounded-lg border border-borda bg-sup-2 py-0.5 pr-0.5 pl-2 text-sm" data-email-destino={x} data-externo={dentro.has(x) ? undefined : ''}>
                <span className="min-w-0 truncate" title={emailFmt(x)}>
                  {emailFmt(x)}
                </span>
                {!dentro.has(x) && <Selo cor="laranja">{t('crm.crm6.email.fora')}</Selo>}
                <button
                  type="button"
                  className="grid size-7 place-items-center rounded text-tinta-3 hover:bg-sup-3 hover:text-tinta-1"
                  aria-label={t('crm.crm6.email.remover', { email: emailFmt(x) })}
                  title={t('crm.crm6.email.remover', { email: emailFmt(x) })}
                  onClick={() => aoMudar(valor.filter((y) => y !== x))}
                  data-remover-destino={x}
                >
                  <X className="size-3.5" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
        <Entrada
          id={id}
          type="text"
          inputMode="email"
          autoComplete="off"
          list={`${id}-sugestoes`}
          placeholder={t('crm.crm6.email.digite')}
          value={texto}
          aria-invalid={erro ? true : undefined}
          aria-describedby={erro ? `${id}-erro` : ajuda ? `${id}-ajuda` : undefined}
          onChange={(e) => {
            const v = e.target.value;
            // Escolher uma sugestão (ou colar a lista) já confirma; vírgula e ponto e vírgula também.
            if (/[,;]\s*$/.test(v) || livres.includes(v.trim().toLowerCase())) confirmar(v);
            else setTexto(v);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              confirmar();
            } else if (e.key === 'Backspace' && !texto && valor.length) aoMudar(valor.slice(0, -1));
          }}
          onBlur={() => texto.trim() && confirmar()}
          data-email-entrada={dado}
        />
        {/* Rodada rlote-2 (VIS-M1): no modo discreto as sugestões não abrem a lista com os endereços por extenso (o valor de
            uma opção do datalist é o próprio texto); a pessoa digita ou o "Para" já vem preenchido. */}
        <datalist id={`${id}-sugestoes`}>
          {!discretoAtivo() &&
            livres.map((x) => (
              <option key={x} value={x} />
            ))}
        </datalist>
      </Rotulo>
    </div>
  );
}
